import React, {useCallback, useReducer, useState, Suspense} from 'react';
import moment from 'moment-timezone';
import {produce} from 'immer';
import i18next from 'i18next';

import * as GlobalEvent from 'web-app/services/global-event';
import * as SentryService from 'web-app/services/sentry';
import * as Famlytics from 'web-app/util/famlytics/index';
import {TrackingEvents} from 'web-app/util/famlytics/events';
import {isFailed} from 'web-app/util/failable';
import {useDialog} from '@famly/mf_modals-dialogs_context';

import {type State} from './types';
import {type Child} from './helpers/plan-types';
import {DEFAULT_STATE} from './constants';
import {reducer} from './reducer';
import {type DateRangeInclusive, convertInclusiveToExclusive} from './helpers/date-range';
import {addBreakToChild} from './helpers/plan-breaker';
import {patchMissingSessionBookingTimes} from './helpers/patch-missing-session-booking-times';
import Spinner from '../components/loading/spinner/spinner';
import {useCreatePlansMutation} from './hooks/use-create-plans-mutation';
import {useGetSessionAvailability} from './hooks/use-get-session-availability';

const ScheduleBreakModal = React.lazy(() =>
    import('./schedule-break-modal').then(mod => ({
        default: mod.ScheduleBreakModal,
    })),
);

type Props = {
    siteId: string;
    children: Child[];
    isLoading: boolean;
    onClose: () => void;
    shouldConfirm?: boolean;
    firstUnbilledDate: string | null;
};

const Loader = (props: Props) => {
    const stateInitializer = useCallback(
        (initialState: State) =>
            produce(initialState, draftState => {
                draftState.dateRange.fromInclusive = props.firstUnbilledDate
                    ? moment(props.firstUnbilledDate)
                    : moment();
                draftState.dateRange.toExclusive = null;
                return draftState;
            }),
        [props.firstUnbilledDate],
    );
    const [state, dispatch] = useReducer(reducer, DEFAULT_STATE, stateInitializer);
    const canContinuePlan = state.dateRange.toExclusive !== null;

    const handleSetDateRange = (range: DateRangeInclusive) => {
        dispatch({type: 'SET_DATE_RANGE', payload: {dateRange: convertInclusiveToExclusive(range)}});
    };
    const handleToggleContinuePlan = (continuePlan: boolean) => {
        dispatch({type: 'SET_CONTINUE_PLAN', payload: {continuePlan}});
    };

    const handleUpdatePlanValidationErrors = useCallback(
        (validationErrors: string[]) => {
            dispatch({type: 'UPDATE_PLAN_VALIDATION_ERRORS', payload: {validationErrors}});
        },
        [dispatch],
    );

    const {createPlans} = useCreatePlansMutation(props.siteId);

    const {loading: isLoadingSessions, sessions} = useGetSessionAvailability(props.siteId);

    const [isStoring, setIsStoring] = useState(false);

    const {showDialog, hideDialog} = useDialog({
        lazyChildren: () => import('./components/schedule-break-confirmation-dialog'),
    });

    const handleSaveWithConfirmation = () => {
        if (props.shouldConfirm) {
            showDialog({
                onConfirm: () => {
                    hideDialog();
                    handleSave();
                },
                onCancel: () => hideDialog(),
            });
        } else {
            handleSave();
        }
    };

    const handleSave = () => {
        setIsStoring(true);

        Famlytics.track(TrackingEvents.SCHEDULE_BREAK_MODAL_SAVE, {
            continue: state.continuePlan,
            children: props.children.length,
            startDate: state.dateRange.fromInclusive.format('YYYY-MM-DD'),
            days:
                state.dateRange.toExclusive !== null
                    ? state.dateRange.toExclusive.diff(state.dateRange.fromInclusive, 'days')
                    : undefined,
        });

        const patchedChildren = patchMissingSessionBookingTimes(props.children, sessions!);
        if (isFailed(patchedChildren)) {
            GlobalEvent.updateError(i18next.t('react.scheduleBreak.translations.json.couldNotCreateBreaks'));
            SentryService.captureMessage(`Failed to path session bookings: ${patchedChildren.error}`);
            return;
        }
        const updatedChildren = patchedChildren.value.map(child =>
            addBreakToChild(child, state.dateRange, state.continuePlan),
        );
        const planInputs = updatedChildren.flatMap(child => child.plans);
        const promise = createPlans(planInputs, props.children, state.dateRange);
        promise
            .then(() => {
                setIsStoring(false);
                GlobalEvent.updateSuccess(
                    i18next.t('react.scheduleBreak.translations.json.breakSuccessfullyCreated', {
                        count: updatedChildren.length,
                    }),
                );
                props.onClose();
            })
            .catch(reason => {
                setIsStoring(false);
                handleUpdatePlanValidationErrors(reason);

                GlobalEvent.updateError(i18next.t('react.scheduleBreak.translations.json.couldNotCreateBreaks'));
                SentryService.captureMessage(`Failed to create breaks. The reason is ${reason.join(', ')}`);
            });
    };
    const handleCancel = () => {
        Famlytics.track(TrackingEvents.SCHEDULE_BREAK_MODAL_CANCEL);

        props.onClose();
    };

    return (
        <Suspense fallback={<Spinner centered margined />}>
            <ScheduleBreakModal
                state={state}
                canContinuePlan={canContinuePlan}
                handleSetDateRange={handleSetDateRange}
                handleToggleContinuePlan={handleToggleContinuePlan}
                handleSave={handleSaveWithConfirmation}
                handleCancel={handleCancel}
                isLoading={props.isLoading || isStoring || isLoadingSessions}
                firstUnbilledDate={props.firstUnbilledDate}
                validationErrors={state.validationErrors}
            />
        </Suspense>
    );
};

export {Loader as ScheduleBreakModal};
